import React, { useState, useMemo, useCallback } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import { fetchABNPayments, createABNBank, downloadAbncalculations_, downloadAbnpurchaseorder_ } from '../utils/api';
import { cssUrl } from '../utils/cssVersion';
import { useUser } from '../utils/UserContext';
import HelpButton from '../components/shared/HelpButton';
import HoldPaymentsModal from '../components/HoldPaymentsModal';
import { DollarIcon2, PurchaseOrderIcon } from '../components/shared/Icons';
import { rolesAccess } from '../data/map';
const filterLeaveStyles = cssUrl('filterTemplate.css');

/* -------------------------------------------------------------------------
 * Requires the one-line FilterTemplate change:
 *   {renderExtraActions && renderExtraActions({ selectedMonthYear, chipQuery, setToast, getData })}
 * ---------------------------------------------------------------------- */

/* Chip label -> Airtable "Payment Status" string (index 3).
 * CONFIRM these against your data. PAYMENTS_STATUS must equal the backend
 * constant of the same name, and must differ from the "Pending" chip. */
const PAYMENTS_STATUS = 'Yes Invoice'; // the chip that unlocks the bank file
const CHIPS = [
  { label: 'Paid', value: 'Paid' },
  { label: 'Pending', value: 'Pending Payment' },
  { label: 'Payments', value: PAYMENTS_STATUS },
  { label: 'No Approval', value: 'Waiting Approval' },
  { label: 'No Invoice', value: 'No Invoice' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Waiting Invoice', value: 'Waiting For Invoice' },
  { label: 'ON HOLD', value: 'ON HOLD' },
  { label: 'PO Document', value: 'Creating PO Document' },
  { label: 'Purchase Order', value: 'Send Purchase Order' },
];

/* Accent colour per status (active chips + status tag get these inline,
 * matching the "colours come from inline styles" note in the CSS). */
const COLOR = {
  Paid: '#4ade80',
  'Pending Payment': '#fbbf24',
  'Yes Invoice': '#22d3ee',
  'Waiting Approval': '#60a5fa',
  'No Invoice': '#f87171',
  'In Progress': '#818cf8',
  'Waiting For Invoice': '#c084fc',
  'ON HOLD': '#9ca3af',
  'Creating PO Document': '#1589FF',
  'Send Purchase Order': '#1589FF',
  HPN: '#fb923c',
};
const accent = (status) => COLOR[status] || '#22d3ee';

/* Raw rows are arrays from the backend; map by index. */
const IDX = {
  id: 0, email: 1, endOfMonth: 2, status: 3, poNum: 4,
  poValue: 5, gst: 6, incGst: 7, business: 8, recordId: 9,
  bankName: 10, bsb: 11, accountNum: 12, incGstRaw: 13, recordId1: 14,
};

const norm = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, '');
const parseCurrency = (s) => {
  if (typeof s === 'number') return s;
  const n = parseFloat(String(s ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isApproved = (status) => !['Waiting Approval', 'ON HOLD', 'In Progress'].includes(status);

/* Airtable synced-table settle — the PO table this page reads is a sync of
 * the subbie source the aba flow writes (Paid + HPN revert). Sync delay
 * varies, so poll the refetch until the data actually changes instead of
 * guessing with a fixed sleep. */
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 10; // ~25s worst case before giving up politely
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/* layout-only inline styles (theming all comes from filterTemplate.css) */
const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const totalsWrap = { display: 'flex', gap: 8, padding: '4px 16px 8px' };
const totalPill = {
  flex: '1 1 0', minWidth: 0, textAlign: 'center', borderRadius: 12, padding: '8px 10px',
  background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.25)',
};
const totalLabel = { fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const totalValue = { fontSize: '0.95rem', fontWeight: 700, color: '#22d3ee', whiteSpace: 'nowrap' };

const Cell = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div className="fr-card-label">{label}</div>
    <div className="fr-card-value">{value}</div>
  </div>
);

const BankAction = ({ monthYear, chipQuery, setToast, refetch, paymentRows }) => {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  /* paymentRows updates after each refetch; the poll loop reads this ref */
  const rowsRef = React.useRef(paymentRows);
  React.useEffect(() => {
    rowsRef.current = paymentRows;
  }, [paymentRows]);

  const onClick = async () => {
    if (norm(chipQuery) !== norm(PAYMENTS_STATUS)) {
      setToast({ message: 'Select the "Payments" chip first to generate the bank file.', type: 'error' });
      return;
    }
    try {
      setBusy(true);

      // snapshot the Payments ids BEFORE the download — these should
      // disappear from the synced table once the Paid flips land
      const beforeIds = (rowsRef.current || []).map((r) => r[IDX.recordId]);

      const { blob, approvedCount } = await createABNBank({ monthYear });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payroll_subbie.aba';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      if (approvedCount > 0 && beforeIds.length > 0) {
        /* Paid flips + HPN revert were written to the subbie source — poll
         * the synced PO table until at least one pre-download Payments
         * record has left the chip, i.e. the sync has landed. */
        setSyncing(true);
        setToast({ message: 'Bank file downloaded. Syncing statuses with Database…', type: 'success' });
        let cleared = false;
        for (let i = 0; i < MAX_POLLS; i++) {
          await sleep(POLL_INTERVAL_MS);
          await refetch();
          await sleep(300); // let the refetched rows propagate into props
          const currentIds = new Set((rowsRef.current || []).map((r) => r[IDX.recordId]));
          if (beforeIds.some((id) => !currentIds.has(id))) {
            cleared = true;
            break;
          }
        }
        setToast({
          message: cleared
            ? `${approvedCount} record${approvedCount === 1 ? '' : 's'} approved to Paid.`
            : `${approvedCount} record${approvedCount === 1 ? '' : 's'} approved to Paid at the source, but Airtable's sync is running slow — refresh in a moment to see updated statuses.`,
          type: cleared ? 'success' : 'error',
        });
      } else {
        await refetch();
        setToast({
          message: `Bank file downloaded. ${approvedCount} record${approvedCount === 1 ? '' : 's'} approved to Paid.`,
          type: 'success',
        });
      }
    } catch (e) {
      setToast({ message: e.message || 'Failed to generate bank file', type: 'error' });
    } finally {
      setSyncing(false);
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="fr-download-btn"
      onClick={onClick}
      disabled={busy}
      title={syncing ? 'Syncing statuses with Database…' : 'Generate bank (.aba) file for the Payments chip'}
      style={busy ? { opacity: 0.5 } : undefined}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="21" x2="21" y2="21" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <polyline points="5 6 12 3 19 6" />
        <line x1="4" y1="10" x2="4" y2="21" />
        <line x1="20" y1="10" x2="20" y2="21" />
        <line x1="8" y1="14" x2="8" y2="17" />
        <line x1="12" y1="14" x2="12" y2="17" />
        <line x1="16" y1="14" x2="16" y2="17" />
      </svg>
    </button>
  );
};

const DownloadAction = ({ monthYear, setToast, fetchFile, filename, title, icon }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    try {
      setBusy(true);
      const blob = await fetchFile(monthYear);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setToast({ message: e.message || 'Download failed', type: 'error' });
    } finally { setBusy(false); }
  };
  return (
    <button type="button" className="fr-download-btn" onClick={onClick} disabled={busy}
            title={title} style={busy ? { opacity: 0.5 } : undefined}>
      {icon}
    </button>
  );
};

/* Icon button next to the help button. Only opens the HPN modal when the
 * Payments chip is active — otherwise a toast tells the user to select it. */
const HoldAction = ({ chipQuery, setToast, onOpen }) => {
  const onClick = () => {
    if (norm(chipQuery) !== norm(PAYMENTS_STATUS)) {
      setToast({ message: 'Select the "Payments" chip first to manage payment holds.', type: 'error' });
      return;
    }
    onOpen();
  };
  return (
    <button type="button" className="fr-download-btn" onClick={onClick}
            title="Hold payments before generating the bank file">
      {/* pause-in-circle icon */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
        <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
        <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
      </svg>
    </button>
  );
};

const ReadOnlyModal = ({ item, onClose }) => {
  const Field = ({ label, value }) => (
    <div className="fr-modal-field">
      <span className="fr-modal-label">{label}</span>
      <div className="fr-modal-value">{value ?? '—'}</div>
    </div>
  );

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Subbie Payment</h3>
          <button className="fr-modal-close" onClick={onClose} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="fr-modal-body">
          <Field label="Business Name" value={item[IDX.business]} />
          <Field label="End Of Month" value={item[IDX.endOfMonth]} />
          <Field label="Email" value={item[IDX.email]} />
          <div style={row2}>
            <Field label="PO Number" value={item[IDX.poNum]} />
            <Field label="Payment Status" value={item[IDX.status]} />
          </div>
          <div style={row2}>
            <Field label="PO Value" value={item[IDX.poValue]} />
            <Field label="GST" value={item[IDX.gst]} />
          </div>
          <Field label="Inc GST" value={item[IDX.incGst]} />
          <Field label="Bank Name" value={item[IDX.bankName]} />
          <div style={row2}>
            <Field label="BSB" value={item[IDX.bsb]} />
            <Field label="Account Number" value={item[IDX.accountNum]} />
          </div>
        </div>

        <div className="fr-modal-actions">
          <button type="button" className="fr-modal-btn fr-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SubbiePayments = ({ showHeader = false }) => {
  const [visible, setVisible] = useState([]);
  const [holdOpen, setHoldOpen] = useState(false);
  const { user } = useUser();
  const isIT = rolesAccess.it.includes(user?.userJob || '');
  const isManagement = rolesAccess.management.includes(user?.userJob || '');

  const totals = useMemo(
    () =>
      visible.reduce(
        (acc, r) => {
          acc.poValue += parseCurrency(r[IDX.poValue]);
          acc.gst += parseCurrency(r[IDX.gst]);
          acc.incGst += parseCurrency(r[IDX.incGst]);
          return acc;
        },
        { poValue: 0, gst: 0, incGst: 0 }
      ),
    [visible]
  );

  /* Rows fed to the hold modal: the currently visible rows, hard-filtered to
   * the Payments status for safety (visible already is, when the chip is on). */
  const paymentRows = useMemo(
    () => visible.filter((r) => norm(r[IDX.status]) === norm(PAYMENTS_STATUS)),
    [visible]
  );

  /* ---- chips (mobile-collapsible + desktop) + total pills ---- */
  const renderChips = useCallback(
    (chipQuery, setChipQuery, chipsOpen, setChipsOpen) => {
      const count = chipQuery ? 1 : 0;
      const buttons = CHIPS.map((c) => {
        const active = norm(chipQuery) === norm(c.value);
        const color = accent(c.value);
        return (
          <button
            key={c.value}
            type="button"
            className={`fr-chip${active ? ' fr-chip-active' : ''}`}
            style={active ? { background: `${color}26`, color, borderColor: `${color}80` } : undefined}
            onClick={() => setChipQuery(active ? '' : c.value)}
          >
            {c.label}
          </button>
        );
      });

      return (
        <>
          <div className="fr-chips-desktop">
            <div className="fr-filter-chips">{buttons}</div>
          </div>

          <div className="fr-chips-mobile">
            <button type="button" className="fr-chips-toggle" onClick={() => setChipsOpen((o) => !o)}>
              <span className="fr-chips-toggle-text">
                Payment Filters
                {count > 0 && <span className="fr-chips-badge">{count}</span>}
              </span>
              <span className="fr-chips-toggle-hint">{chipsOpen ? 'Tap to hide' : 'Tap to filter'}</span>
            </button>
            <div className={`fr-chips-mobile-panel${chipsOpen ? ' fr-chips-open' : ''}`}>
              <div className="fr-filter-chips">{buttons}</div>
            </div>
          </div>

          <div style={totalsWrap}>
            <div style={totalPill}><div style={totalLabel}>PO Value</div><div style={totalValue}>{fmt(totals.poValue)}</div></div>
            <div style={totalPill}><div style={totalLabel}>GST</div><div style={totalValue}>{fmt(totals.gst)}</div></div>
            <div style={totalPill}><div style={totalLabel}>Inc GST</div><div style={totalValue}>{fmt(totals.incGst)}</div></div>
          </div>
        </>
      );
    },
    [totals]
  );

  /* ---- card ---- */
  const renderCard = useCallback((item, onCardClick) => {
    const status = item[IDX.status];
    const color = accent(status);
    return (
      <div className="fr-card" role="button" onClick={() => onCardClick(item)}>
        {/* row 1: Business Name (span 2) | End of Month */}
        <div style={row3}>
          <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
            <div className="fr-card-label">Business Name</div>
            <div className="fr-card-value">{item[IDX.business]}</div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="fr-card-label">End of Month</div>
            <div className="fr-card-value fr-card-date-end">{item[IDX.endOfMonth]}</div>
          </div>
        </div>

        {/* row 2: PO Num | Pay Status | Approved */}
        <div style={row3}>
          <Cell label="PO Num" value={item[IDX.poNum]} />
          <div style={{ minWidth: 0 }}>
            <div className="fr-card-label">Pay Status</div>
            <span className="fr-card-status-tag" style={{ color, background: `${color}26` }}>{status}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="fr-card-label">Approved</div>
            <div className="fr-card-value" style={{ color: isApproved(status) ? '#4ade80' : '#9ca3af' }}>
              {isApproved(status) ? 'Yes' : 'No'}
            </div>
          </div>
        </div>

        {/* row 3: PO Value | GST | Inc GST */}
        <div style={{ ...row3, marginBottom: 0 }}>
          <Cell label="PO Value" value={item[IDX.poValue]} />
          <Cell label="GST" value={item[IDX.gst]} />
          <Cell label="Inc GST" value={item[IDX.incGst]} />
        </div>

        <div className="fr-card-tap-hint">Tap to view details</div>
      </div>
    );
  }, []);

  const renderModal = useCallback(
    (item, onClose) => <ReadOnlyModal item={item} onClose={onClose} />,
    []
  );

  const renderExtraActions = useCallback(
    ({ selectedMonthYear, chipQuery, setToast, getData }) => (
      <>
        <HoldAction
          chipQuery={chipQuery}
          setToast={setToast}
          onOpen={() => setHoldOpen(true)}
        />
        <BankAction
          monthYear={selectedMonthYear}
          chipQuery={chipQuery}
          setToast={setToast}
          refetch={() => getData(selectedMonthYear, false)}
          paymentRows={paymentRows}
        />
        <DownloadAction
          monthYear={selectedMonthYear}
          setToast={setToast}
          fetchFile={downloadAbncalculations_}
          filename={`abnpaymentscalculations_${selectedMonthYear}.xlsx`}
          title="Download Contractor Calculations"
          icon={<DollarIcon2 />}
        />
        <DownloadAction
          monthYear={selectedMonthYear}
          setToast={setToast}
          fetchFile={downloadAbnpurchaseorder_}
          filename={`abnpaymentspurchaseorder_${selectedMonthYear}.xlsx`}
          title="Download Purchase Orders"
          icon={<PurchaseOrderIcon />}
        />
        {holdOpen && (
          <HoldPaymentsModal
            rows={paymentRows}
            setToast={setToast}
            onClose={() => setHoldOpen(false)}
            refetch={() => getData(selectedMonthYear, false)}
          />
        )}
      </>
    ),
    [holdOpen, paymentRows]
  );

  return (
    <FilterTemplate
      helpButton={
        <HelpButton helpData={[
          { chip: 'bank_filter', urlFilter: '/static/pdffilters/SubbiePayments_Guide.pdf' }
        ]} />
      }
      title="Subbie Payments"
      showHeader={showHeader}
      stylesheetHref={filterLeaveStyles}
      fetchData={fetchABNPayments}
      dataKey="abnpayments"
      searchFields={[IDX.status]}           // chips match status (exact)
      typingSearchFields={[IDX.business]}   // typed search matches Business Name
      searchPlaceholder="Search business name…"
      renderChips={renderChips}
      renderCard={renderCard}
      renderModal={renderModal}
      renderExtraActions={renderExtraActions}
      onFilteredChange={setVisible}
      emptyIcon="💸"
      emptyTitle="No Subbie Payments"
      loadingMessage="Loading payments…"
    />
  );
};

export default SubbiePayments;